/**
 * "From my library": what every loader at the table opens (v11.71.0) - the
 * library's own folders, showing only the kind this loader takes, a search,
 * and the one button that copies (or applies) the chosen item. A copy says
 * so in the list, and says when the library has a newer version.
 *
 * It opens over the whole page (a portal to the body): rendered inside the
 * handouts drawer, its fixed backdrop was the drawer's width and the dialog
 * was cut off (2026-10-20). Wide, the folders beside the items.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import useEscapeKey from '../../hooks/useEscapeKey';
import { childrenOf, pathOf, under } from '../../engine/assets/tree';
import { matches } from '../../engine/assets/listing';
import { isWhole } from '../../engine/assets/rows';
import { ROOT, type AssetKind, type AssetRow, type FolderRow, type Origin } from '../../engine/assets/types';
import type { Library } from '../../assetStore';
import AssetThumb from './AssetThumb';
import AssetGlyph from './AssetGlyph';
import styles from '../../styles/Assets.module.css';

interface Props {
  lib: Library;
  /** The library's owner, for its cards' pictures; without it each card shows its kind's drawing. */
  uid?: string;
  /** The kind this loader takes, or the kinds (the handouts drawer takes images and handouts). */
  kind: AssetKind | readonly AssetKind[];
  title: string;
  /** What an empty list says instead of "Nothing of this kind here yet." - why, where there is a reason. */
  empty?: string;
  /** What the button says: "Copy into the session", "Apply to this map". */
  action: string;
  /** The session's copies, so the list can mark them. */
  copies?: readonly { origin?: Origin | null }[];
  /** Where those copies are, in the mark: "in session" (the default), "in scene" (the scene editor's cast). */
  copiedIn?: string;
  onPick(row: AssetRow): void;
  /** Several at once (the handouts drawer); without it the picker takes one. */
  many?: PickMany;
  onClose(): void;
}

/** Several at once: what the button says for n ticked, and what takes them, in the library's order. */
interface PickMany {
  label(n: number): string;
  onPick(rows: AssetRow[]): void;
}

function FolderList({
  folders,
  pool,
  at,
  setAt,
}: {
  folders: FolderRow[];
  pool: AssetRow[];
  at: string;
  setAt(id: string): void;
}) {
  const holds = (id: string) => pool.some((a) => under(folders, a.folderId, id));
  const node = (f: FolderRow | null, depth: number): ReactNode => {
    const id = f?.id ?? ROOT;
    if (!holds(id)) return null;
    return (
      <div key={id || 'root'}>
        <button
          type="button"
          className={styles.node}
          style={{ paddingLeft: 6 + depth * 12 }}
          aria-current={at === id}
          onClick={() => setAt(id)}
        >
          {f ? f.name : 'My assets'}
        </button>
        {childrenOf(folders, id).map((k) => node(k, depth + 1))}
      </div>
    );
  };
  return <div className={styles.pickFolders}>{node(null, 0)}</div>;
}

/** The whole, live assets of the kinds asked for. */
function poolOf(lib: Library, kind: Props['kind']): AssetRow[] {
  const kinds: readonly AssetKind[] = typeof kind === 'string' ? [kind] : kind;
  return lib.assets.filter((a) => kinds.includes(a.kind) && isWhole(a));
}

function copyNote(row: AssetRow, copies: Props['copies'], copiedIn = 'session'): string {
  const copy = copies?.find((c) => c.origin?.assetId === row.id);
  if (!copy?.origin) return '';
  return copy.origin.version < row.version ? `in ${copiedIn} as v${copy.origin.version}` : `in ${copiedIn}`;
}

interface CardProps {
  uid: string | undefined;
  row: AssetRow;
  /** "v2 in session · Handbills": the version, the session's copy, the folder. */
  facts: string;
  chosen: boolean;
  onChoose(): void;
  onPick(): void;
}

/**
 * One item as a card: its picture (the shot the library page shows, or its kind's drawing), its name, and its facts
 * (2026-10-20: a list of names could not tell pictures apart).
 */
function PickCard({ uid, row, facts, chosen, onChoose, onPick }: CardProps) {
  return (
    <div
      role="option"
      tabIndex={0}
      aria-selected={chosen}
      className={styles.pickItem}
      data-testid={`library-pick-item-${row.id}`}
      onClick={onChoose}
      onDoubleClick={onPick}
      onKeyDown={(e) => e.key === 'Enter' && onPick()}
    >
      {uid ? (
        <AssetThumb uid={uid} asset={row} className={styles.pickThumb} />
      ) : (
        <AssetGlyph kind={row.kind} className={styles.pickThumb} />
      )}
      <span className={styles.pickName} title={row.name}>
        {row.name}
      </span>
      <span className={styles.count}>{facts}</span>
    </div>
  );
}

/** The card's facts line. */
const factsOf = (lib: Library, row: AssetRow, copies: Props['copies'], copiedIn?: string): string =>
  [`v${row.version}`, copyNote(row, copies, copiedIn), pathOf(lib.folders, row.folderId).at(-1)?.name ?? '']
    .filter(Boolean)
    .join(' · ');

/**
 * What is ticked: one at a time (a click moves the choice), or several (a click ticks or unticks; "Select all here"
 * ticks everything shown). Ticks outlive a change of folder or search, so a pick can gather from several folders.
 */
function usePicked(multiple: boolean) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const choose = (id: string) =>
    setPicked((was) => {
      if (!multiple) return new Set([id]);
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const all = (rows: readonly AssetRow[]) => setPicked((was) => new Set([...was, ...rows.map((r) => r.id)]));
  return { picked, choose, all };
}

interface ActionsProps {
  action: string;
  many: PickMany | undefined;
  count: number;
  canAll: boolean;
  onAll(): void;
  onConfirm(): void;
  onCancel(): void;
}

/** The footer: Select all here (several at once only), Cancel, and the button that takes what is ticked. */
function PickActions({ action, many, count, canAll, onAll, onConfirm, onCancel }: ActionsProps) {
  return (
    <div className={styles.acts} style={{ justifyContent: 'flex-end' }}>
      {many && (
        <button
          type="button"
          className={styles.pickAll}
          disabled={!canAll}
          data-testid="library-pick-all"
          onClick={onAll}
        >
          Select all here
        </button>
      )}
      <button type="button" className={styles.btn} onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className={styles.btn}
        disabled={count === 0}
        data-testid="library-pick-confirm"
        onClick={onConfirm}
      >
        {many ? many.label(count) : action}
      </button>
    </div>
  );
}

export default function LibraryPicker(props: Props) {
  const { lib, uid, kind, title, empty, action, copies, copiedIn, onPick, many, onClose } = props;
  const [at, setAt] = useState(ROOT);
  const [query, setQuery] = useState('');
  const { picked, choose, all } = usePicked(!!many);
  useEscapeKey(
    true,
    useCallback(() => onClose(), [onClose]),
  );
  const pool = poolOf(lib, kind);
  const shown = pool.filter((a) => under(lib.folders, a.folderId, at) && matches(a, query));
  const rows = pool.filter((a) => picked.has(a.id));
  const take = (taken: AssetRow[]) => (many ? many.onPick(taken) : taken[0] && onPick(taken[0]));
  return createPortal(
    // A dialog of its own: its clicks stop here. From the body, the modal it opened from would read them as clicks
    // outside its box (useModalBackdrop) and close, the picker with it - React bubbles a portal's events up its tree.
    <div className={styles.pickBackdrop} role="presentation" onClick={(e) => e.stopPropagation()}>
      <div
        className={styles.pickDialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="library-pick-dialog"
      >
        <div className={styles.pickHead}>
          <strong className={styles.sideTitle}>{title}</strong>
          <input
            type="search"
            className={styles.search}
            placeholder="Search"
            aria-label="Search the library"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className={styles.pickBody}>
          <FolderList folders={lib.folders} pool={pool} at={at} setAt={setAt} />
          <div
            className={styles.pickItems}
            role="listbox"
            aria-label="Library items"
            aria-multiselectable={many ? true : undefined}
          >
            {shown.length === 0 && <p className={styles.note}>{empty ?? 'Nothing of this kind here yet.'}</p>}
            {shown.map((a) => (
              <PickCard
                key={a.id}
                uid={uid}
                row={a}
                facts={factsOf(lib, a, copies, copiedIn)}
                chosen={picked.has(a.id)}
                onChoose={() => choose(a.id)}
                onPick={() => take([a])}
              />
            ))}
          </div>
        </div>
        <PickActions
          action={action}
          many={many}
          count={rows.length}
          canAll={shown.length > 0}
          onAll={() => all(shown)}
          onConfirm={() => take(rows)}
          onCancel={onClose}
        />
      </div>
    </div>,
    document.body,
  );
}
